import { entityConfigs } from '@ast-data/shared';
import { defineStoredJson } from '../../utils/storage';

/**
 * What this device itself has sent, so a conflict with its own earlier state
 * is not shown as somebody else's change.
 *
 * Several tabs replicate the same database at once (the leader and the
 * visible tab, see `isReplicatingHere`). Both read the same assumed server
 * state, so when a record is changed twice in quick succession one tab sends
 * the first state and the other the second against the now outdated base —
 * and the server answers with a conflict between the device and itself.
 *
 * Kept in localStorage because the tab that sent and the tab that meets the
 * conflict are usually not the same one.
 *
 * Everything here fails towards the dialog: a lost, evicted or unreadable
 * record only means the person is asked, as before. It must never stop a
 * push — see the guards in both entry points.
 */

/** One push of one document: hashes of the fields it changed. */
interface SentState {
    key: string;
    at: number;
    fields: Record<string, string>;
}

/**
 * The race is a matter of seconds; every push attempt records afresh, so a
 * retry after an outage is covered too. Kept short because within it a value
 * somebody else happens to set back to ours would count as ours.
 */
const KEEP_MS = 10 * 60 * 1000;
/** Bounds what an import can put into localStorage, which the journal needs. */
const MAX_STATES = 500;

function isSentState(value: unknown): value is SentState {
    const state = value as SentState | null;
    return (
        typeof state === 'object' &&
        state !== null &&
        typeof state.key === 'string' &&
        typeof state.at === 'number' &&
        typeof state.fields === 'object' &&
        state.fields !== null
    );
}

const sentStates = defineStoredJson<SentState[]>({
    key: 'sent_states',
    fallback: () => [],
    // A safeguard against needless dialogs, never work: a wipe may take it.
    decode: (parsed) =>
        Array.isArray(parsed) ? parsed.filter(isSentState) : undefined
});

/** What this needs of an entity, without the union of every wire type. */
interface SentEntity {
    conflictFields: readonly string[];
    sanitize(doc: Record<string, unknown>): Record<string, unknown>;
}

const entityByCollection = new Map(
    entityConfigs.map((entity) => [
        entity.collection,
        entity as unknown as SentEntity
    ])
);

function stateKey(collection: string, docId: string): string {
    return `${collection}/${docId}`;
}

/** Same notion of "equal" as `valuesEqual`: plain arrays compare sorted. */
function normalize(value: unknown): unknown {
    if (value === undefined) {
        return null;
    }
    return Array.isArray(value) ? [...value].sort() : value;
}

function stableStringify(value: unknown): string {
    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(',')}]`;
    }
    if (value && typeof value === 'object') {
        const entries = Object.keys(value as Record<string, unknown>)
            .sort()
            .map(
                (name) =>
                    `${JSON.stringify(name)}:${stableStringify((value as Record<string, unknown>)[name])}`
            );
        return `{${entries.join(',')}}`;
    }
    return JSON.stringify(value ?? null);
}

/**
 * cyrb53, 53 bits. A collision would take somebody else's value for ours and
 * overwrite it without asking — keep the hash this wide; it is compared only
 * among the few states of one document.
 */
function hashValue(value: unknown): string {
    const text = stableStringify(normalize(value));
    let first = 0xdeadbeef;
    let second = 0x41c6ce57;
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        first = Math.imul(first ^ code, 2654435761);
        second = Math.imul(second ^ code, 1597334677);
    }
    first =
        Math.imul(first ^ (first >>> 16), 2246822507) ^
        Math.imul(second ^ (second >>> 13), 3266489909);
    second =
        Math.imul(second ^ (second >>> 16), 2246822507) ^
        Math.imul(first ^ (first >>> 13), 3266489909);
    return (4294967296 * (2097151 & second) + (first >>> 0)).toString(36);
}

/**
 * Note the documents of a push before it goes out. Only the fields that
 * differ from the assumed server state — only those can come back as a
 * conflict. Recorded BEFORE sending: the other tab may meet the result
 * before this one has read the answer.
 */
export function rememberSentStates(
    collection: string,
    primaryPath: string,
    rows: {
        newDocumentState: Record<string, unknown>;
        assumedMasterState?: Record<string, unknown>;
    }[]
): void {
    try {
        record(collection, primaryPath, rows);
    } catch (error) {
        console.warn('[sync] sent states not recorded:', error);
    }
}

function record(
    collection: string,
    primaryPath: string,
    rows: {
        newDocumentState: Record<string, unknown>;
        assumedMasterState?: Record<string, unknown>;
    }[]
): void {
    const entity = entityByCollection.get(collection);
    if (!entity || rows.length === 0) {
        return;
    }
    const now = Date.now();
    const added: SentState[] = [];
    for (const { newDocumentState, assumedMasterState } of rows) {
        // The server stores the sanitized form, so that is what comes back.
        let sanitized = newDocumentState;
        try {
            sanitized = entity.sanitize(newDocumentState);
        } catch {
            // Unsanitizable: compare what was sent.
        }
        const fields: Record<string, string> = {};
        for (const field of entity.conflictFields) {
            const hash = hashValue(sanitized[field]);
            if (
                !assumedMasterState ||
                hashValue(assumedMasterState[field]) !== hash
            ) {
                fields[field] = hash;
            }
        }
        added.push({
            key: stateKey(collection, String(newDocumentState[primaryPath])),
            at: now,
            fields
        });
    }
    const kept = sentStates.read().filter((state) => now - state.at < KEEP_MS);
    sentStates.write([...kept, ...added].slice(-MAX_STATES));
}

/**
 * Whether the server's values for these fields are one state this device
 * sent itself — then the "other side" of the conflict is our own earlier
 * edit, and the newer local one simply wins.
 */
export function isOwnSentState(
    collection: string,
    docId: string,
    remote: Record<string, unknown>,
    fields: string[]
): boolean {
    if (fields.length === 0) {
        return false;
    }
    try {
        const key = stateKey(collection, docId);
        const now = Date.now();
        const candidates = sentStates
            .read()
            .filter((state) => state.key === key && now - state.at < KEEP_MS);
        if (candidates.length === 0) {
            return false;
        }
        const remoteHashes = fields.map((field) => hashValue(remote[field]));
        return candidates.some((state) =>
            fields.every(
                (field, index) => state.fields[field] === remoteHashes[index]
            )
        );
    } catch (error) {
        console.warn('[sync] sent states not readable:', error);
        return false;
    }
}
